/**
 * api.ts — lớp gọi API ảnh thẻ của NHÂN VIÊN (client `api` có Bearer + refresh
 * như mọi màn hình nội bộ khác).
 *
 * Màn khách chọn ảnh không còn ở đây: ảnh thẻ chọn theo đơn trên Album của
 * Cổng (QĐ 0398) — màn iPad cầm vé và các lời gọi bằng vé đã gỡ.
 */

import { api } from "@/lib/api/client";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components, paths } from "@/lib/api/generated/schema";

export type Photo = components["schemas"]["Photo"];
export type PhotoState = components["schemas"]["PhotoState"];
export type PhotoEdit = components["schemas"]["PhotoEdit"];
export type PhotoSession = components["schemas"]["PhotoSession"];
export type PhotoCustomerState = components["schemas"]["PhotoCustomerState"];
export type PhotoStaffView = components["schemas"]["PhotoStaffView"];
export type PhotoPrice = components["schemas"]["PhotoPrice"];
export type PhotoComboLine = components["schemas"]["PhotoComboLine"];
export type PhotoRetouchQueueItem = components["schemas"]["PhotoRetouchQueueItem"];
export type PhotoRetouchQueuePhoto = components["schemas"]["PhotoRetouchQueuePhoto"];
export type PhotoRetouchQueueEdit = components["schemas"]["PhotoRetouchQueueEdit"];
export type PhotoShape = components["schemas"]["PhotoShape"];
export type PhotoSelectInput = components["schemas"]["PhotoSelectInput"];
export type PhotoReviewInput = components["schemas"]["PhotoReviewInput"];
export type PhotoAcceptInput = components["schemas"]["PhotoAcceptInput"];
export type PhotoUploadResult = components["schemas"]["PhotoUploadResult"];
export type SignedMediaURL = components["schemas"]["SignedMediaURL"];
export type PhotoSizeRow = components["schemas"]["PhotoSize"];
export type PhotoSizeSpec = components["schemas"]["PhotoSizeSpec"];
export type PhotoSizeOptions = components["schemas"]["PhotoSizeOptions"];
export type PhotoSizeChoice = components["schemas"]["PhotoSizeChoice"];
export type PhotoSizeDimension = components["schemas"]["PhotoSizeDimension"];
export type AddPhotoSizeBody = components["schemas"]["AddPhotoSizeRequest"];
/** Lọc lưới ảnh gốc theo một ô tình trạng (khớp `workCounts`); `total` khi lọc là tổng của ô. */
export type PhotoWorkFilter = NonNullable<
  NonNullable<paths["/orders/{id}/photos"]["get"]["parameters"]["query"]>["work"]
>;
/** Cỡ ảnh xin từ đường đọc. `orig` chỉ dùng khi biến thể chưa sinh xong. */
export type PhotoVariant = "thumb" | "md" | "lg" | "orig";

/** Vé phiên đi trong header này trên MỌI lời gọi của màn khách. */
const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export const photoKeys = {
  /**
   * Tiền tố của MỌI trang ảnh thuộc một đơn.
   *
   * Dùng để vô hiệu hoá cache: `invalidateQueries` khớp theo tiền tố, nên
   * truyền khoá này là quét sạch mọi trang. Truyền khoá của đúng một trang thì
   * các trang khác giữ dữ liệu cũ — tải ảnh xong mà lưới không đổi.
   */
  orderPhotos: (orderId: string) => ["order-photos", orderId] as const,
  /**
   * Một trang cụ thể. Cỡ trang nằm TRONG khoá: đổi cỡ trang mà khoá không đổi
   * thì react-query trả lại đúng trang cũ — người dùng chọn "100 / trang" và
   * vẫn thấy 20 tấm.
   */
  orderPhotosPage: (orderId: string, offset: number, limit: number) =>
    ["order-photos", orderId, offset, limit] as const,
  retouchQueue: () => ["retouch-photo-queue"] as const,
  /**
   * Hàng đợi của MỘT đơn (thẻ "Sửa ảnh" trong chi tiết đơn). Nằm dưới tiền tố
   * `retouchQueue()` nên `invalidateQueries(retouchQueue())` quét luôn khoá này.
   */
  retouchQueueForOrder: (orderId: string) => ["retouch-photo-queue", orderId] as const,
  /**
   * Hàng đợi KÈM tấm xong hôm nay (màn Sửa hình 3 cột). Cũng nằm dưới tiền tố
   * `retouchQueue()`: nộp bản sửa xong quét một khoá là cả ba nơi cùng mới.
   */
  retouchQueueWithDone: () => ["retouch-photo-queue", { doneToday: true }] as const,
  /** Danh mục cỡ ảnh thẻ (QĐ 0285) — ít đổi, dùng chung hộp thợ + trang Sản phẩm. */
  sizeCatalog: () => ["photo-size-catalog"] as const,
  /** 4 hàng lựa chọn + mẫu nhanh của "Thêm bản giao" (QĐ 0303). */
  sizeOptions: () => ["photo-size-options"] as const,
};

// ══════════ NHÂN VIÊN ══════════

function staffError(payload: unknown, fallback: string): Error {
  return new Error(serverErrorMessage(payload, fallback));
}

/** Ảnh gốc của đơn + trạng thái phiên. Chỉ ở đây mới có chữ ký của khách. */
export async function listOrderPhotos(
  orderId: string,
  page: { limit?: number; offset?: number; work?: PhotoWorkFilter } = {},
): Promise<PhotoStaffView> {
  const { data, error } = await api.GET("/orders/{id}/photos", {
    params: {
      path: { id: orderId },
      query: {
        ...(page.limit !== undefined ? { limit: page.limit } : {}),
        ...(page.offset !== undefined ? { offset: page.offset } : {}),
        ...(page.work ? { work: page.work } : {}),
      },
    },
  });
  if (error || !data) throw staffError(error, "Không tải được ảnh của đơn");
  return data;
}

/**
 * Nạp ảnh buổi chụp lên đơn.
 *
 * Đi qua `postFormAuthed` chứ không qua openapi-fetch: thân là FormData, mà
 * client sinh từ schema không mô tả được kiểu thân đó.
 *
 * Trả về CẢ tệp hỏng kèm lý do. Nuốt phần `failed` là để thợ ảnh tưởng đã nạp
 * đủ trong khi thiếu vài tấm — đúng kiểu mất dữ liệu không ai thấy.
 */
/**
 * "Đây là ảnh gì?" của một lượt tải (QĐ 0312): ảnh gốc để khách xem · ảnh khách
 * đã chọn tại quầy (tích sẵn) · ảnh mặt mộc tặng kèm (đúng 1 tệp).
 */
export type PhotoUploadIntent = NonNullable<
  NonNullable<paths["/orders/{id}/photos"]["post"]["parameters"]["query"]>["intent"]
>;

export async function addOrderPhotos(
  orderId: string,
  files: File[],
  intent?: PhotoUploadIntent,
  /** "Ảnh của ai" (chấm QC bản 3, 4a) — chỉ đơn từ hai thợ chụp mới gửi. */
  shotBy?: string,
): Promise<PhotoUploadResult> {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  // Ảnh gốc là mặc định của máy chủ — không kèm tham số, URL y như trước.
  const params = new URLSearchParams();
  if (intent && intent !== "original") params.set("intent", intent);
  if (shotBy) params.set("shotBy", shotBy);
  const query = params.size > 0 ? `?${params.toString()}` : "";
  // Gửi qua XHR để biết thân đã đi hết chưa khi mạng đứt: cửa này KHÔNG chống
  // trùng, nên mẻ tải chỉ gửi lại tấm mà máy chủ chắc chắn chưa nhận.
  const resp = await postFormAuthed(`${apiBaseUrl}/orders/${orderId}/photos${query}`, form, {
    reportBodySent: true,
  });
  if (!resp.ok) throw await uploadError(resp, "Không nạp được ảnh lên đơn");
  return (await resp.json()) as PhotoUploadResult;
}

/**
 * Xin URL đọc một ảnh.
 *
 * Ảnh gốc nằm trong kho riêng tư, nên KHÔNG được trỏ `<img src>` thẳng vào
 * route của BE: FE và API khác origin, cookie phiên là SameSite=Lax nên trình
 * duyệt không gửi kèm khi tải ảnh và nhận 401. Lấy URL đã ký ở đây rồi mới đặt
 * vào `src`.
 */
export async function getPhotoURL(
  orderId: string,
  photoId: string,
  variant: PhotoVariant = "thumb",
): Promise<SignedMediaURL> {
  const { data, error } = await api.GET("/orders/{id}/photos/{photoId}/url", {
    params: { path: { id: orderId, photoId }, query: { v: variant } },
  });
  if (error || !data) throw staffError(error, "Không mở được ảnh");
  return data;
}

/**
 * Xin URL TẢI bản gốc của một ảnh.
 *
 * Đường riêng chứ không phải `getPhotoURL(..., "orig")`: BE bảo kho ảnh trả
 * kèm `Content-Disposition: attachment` nên trình duyệt LƯU thay vì hiện. Thuộc
 * tính `download` của thẻ `<a>` bị bỏ qua khi link khác origin — mà URL ký luôn
 * khác origin với FE — nên header phải đến từ kho ảnh, không phải từ HTML.
 *
 * `name` chỉ là gợi ý phần đầu tên tệp (thường là mã đơn); BE làm sạch rồi ghép
 * số thứ tự ảnh và đuôi tệp vào.
 */
export async function getPhotoDownloadURL(
  orderId: string,
  photoId: string,
  name?: string,
): Promise<SignedMediaURL> {
  const { data, error } = await api.GET("/orders/{id}/photos/{photoId}/download", {
    params: { path: { id: orderId, photoId }, query: name ? { name } : {} },
  });
  if (error || !data) throw staffError(error, "Không tải được ảnh gốc");
  return data;
}

/** Xin URL đọc một bản sửa. Cùng luật với ảnh gốc. */
export async function getPhotoEditURL(
  orderId: string,
  editId: string,
  variant: PhotoVariant = "thumb",
): Promise<SignedMediaURL> {
  const { data, error } = await api.GET("/orders/{id}/photo-edits/{editId}/url", {
    params: { path: { id: orderId, editId }, query: { v: variant } },
  });
  if (error || !data) throw staffError(error, "Không mở được bản sửa");
  return data;
}

// 204 không có thân: openapi-fetch trả `data` rỗng, nên phải soi `response.ok`
// chứ không được dùng khuôn `if (error || !data)` như các endpoint có thân.
export async function deleteOrderPhoto(orderId: string, photoId: string): Promise<void> {
  const { error, response } = await api.DELETE("/orders/{id}/photos/{photoId}", {
    params: { path: { id: orderId, photoId } },
  });
  if (!response.ok) throw staffError(error, "Không gỡ được ảnh khỏi đơn");
}

/** Gỡ nhiều ảnh gốc một lượt (danh sách mã, hoặc cả đơn); ảnh khách đã chốt được bỏ qua. */
export async function bulkDeleteOrderPhotos(
  orderId: string,
  body: { photoIds: string[] } | { all: true },
): Promise<{ deletedCount: number; skippedCount: number }> {
  const { data, error } = await api.POST("/orders/{id}/photos/bulk-delete", {
    params: { path: { id: orderId } },
    body,
  });
  if (!data) throw staffError(error, "Không gỡ được ảnh khỏi đơn");
  return data;
}

/** Mở lại phiên đã nghiệm thu để xử lý khiếu nại (admin/giám đốc). */
export async function reopenPhotoSession(orderId: string): Promise<void> {
  const { error, response } = await api.POST("/orders/{id}/photo-session/reopen", {
    params: { path: { id: orderId } },
  });
  if (!response.ok) throw staffError(error, "Không mở lại được phiên đã nghiệm thu");
}

/**
 * Hàng đợi ảnh chờ thợ sửa — chung toàn quốc, không lọc theo chi nhánh.
 * Truyền `orderId` để chỉ lấy ảnh của một đơn (thẻ "Sửa ảnh" trong chi tiết đơn);
 * phạm vi loại đơn theo người gọi vẫn do máy chủ quyết.
 */
export async function getRetouchQueue(
  orderId?: string,
  opts: { includeDoneToday?: boolean } = {},
): Promise<PhotoRetouchQueueItem[]> {
  const { data, error } = await api.GET("/retouch/photo-queue", {
    params: {
      query: {
        ...(orderId ? { orderId } : {}),
        // Chỉ màn Sửa hình 3 cột xin nhóm "Đã xong hôm nay"; mọi nơi khác
        // không gửi tham số này nên máy chủ giữ nguyên hàng đợi việc còn nợ.
        ...(opts.includeDoneToday ? { includeDoneToday: true } : {}),
      },
    },
  });
  if (error || !data) throw staffError(error, "Không tải được hàng đợi ảnh cần sửa");
  return data;
}

/**
 * Đổi tấm làm bìa của đơn trên màn Sửa hình (QĐ 0226). Chưa ai đổi thì bìa là
 * tấm đầu tiên của đơn — máy chủ tự lấy, màn không phải gửi gì. Máy chủ chỉ
 * nhận tấm thuộc đúng đơn đó.
 */
export async function setOrderPhotoCover(orderId: string, photoId: string): Promise<string> {
  const { data, error } = await api.PUT("/orders/{id}/photo-cover", {
    params: { path: { id: orderId } },
    body: { photoId },
  });
  if (error || !data) throw staffError(error, "Không đổi được ảnh bìa");
  return data.coverPhotoId;
}

/**
 * Thợ nộp một bản sửa. Mỗi lần nộp một tệp, là một phiên bản mới.
 *
 * `sizeCode` — cỡ của bản (QĐ 0285). Tấm đã gắn cỡ thì BẮT BUỘC, tấm không cỡ
 * thì bỏ trống; máy chủ kiểm trước khi đọc tệp. Đi trên đường dẫn vì bộ đọc
 * tệp phía máy chủ bỏ qua mọi ô không phải tệp.
 */
export async function addPhotoEdit(photoId: string, file: File, sizeCode?: string): Promise<PhotoEdit> {
  const form = new FormData();
  form.append("files", file);
  const query = sizeCode ? `?sizeCode=${encodeURIComponent(sizeCode)}` : "";
  const resp = await postFormAuthed(`${apiBaseUrl}/photos/${photoId}/edits${query}`, form);
  if (!resp.ok) throw await uploadError(resp, "Không nộp được bản sửa");
  return (await resp.json()) as PhotoEdit;
}

/** Danh mục cỡ ảnh thẻ (QĐ 0285). Chưa cấu hình thì máy chủ trả mảng rỗng. */
export async function getPhotoSizeCatalog(): Promise<PhotoSizeSpec[]> {
  const { data, error } = await api.GET("/photo-size-catalog");
  if (error || !data) throw staffError(error, "Không tải được danh mục cỡ ảnh");
  return data;
}

/** 4 hàng lựa chọn + mẫu nhanh của bản giao ảnh thẻ (QĐ 0303). */
export async function getPhotoSizeOptions(): Promise<PhotoSizeOptions> {
  const { data, error } = await api.GET("/photo-size-options");
  if (error || !data) throw staffError(error, "Không tải được lựa chọn cỡ ảnh");
  return data;
}

/**
 * Thợ thêm một bản giao (cỡ) cho tấm (QĐ 0285, QĐ 0303). Tấm đã xong thì mở
 * lại. Thân là mẫu nhanh `{sizeCode}` hoặc 4 lựa chọn; máy chủ ghép nhãn và
 * sinh mã (`4x6`, `4x6-2`…) — đọc `code` trả về, đừng tự đoán.
 */
export async function addPhotoSize(photoId: string, body: AddPhotoSizeBody): Promise<PhotoSizeRow> {
  const { data, error } = await api.POST("/photos/{photoId}/sizes", {
    params: { path: { photoId } },
    body,
  });
  if (error || !data) throw staffError(error, "Không thêm được cỡ ảnh");
  return data;
}

/** Thợ bỏ một cỡ chưa có bản sửa nào (QĐ 0285). */
export async function removePhotoSize(photoId: string, sizeCode: string): Promise<void> {
  const { error } = await api.DELETE("/photos/{photoId}/sizes/{sizeCode}", {
    params: { path: { photoId, sizeCode } },
  });
  if (error) throw staffError(error, "Không bỏ được cỡ ảnh");
}

/**
 * Dựng lỗi cho đường tải lên. postFormAuthed trả Response thô nên phải tự đọc
 * thân lỗi, thay vì dùng khuôn `staffError` của openapi-fetch.
 */
async function uploadError(resp: Response, fallback: string): Promise<Error> {
  try {
    return staffError(await resp.json(), fallback);
  } catch {
    return staffError(undefined, fallback);
  }
}
